/**
 * BroadcastChannel-based leader election for SSE connections.
 *
 * Only one browser tab ("the leader") opens an EventSource to /api/events.
 * All other tabs ("followers") receive SSE events via BroadcastChannel,
 * eliminating N redundant backend connections for a user with N tabs open.
 *
 * Leader election protocol:
 * 1. Each tab starts a check loop every LEADER_HEARTBEAT_MS.
 * 2. The leader broadcasts a heartbeat on every tick.
 * 3. Followers track the last received heartbeat timestamp.
 * 4. If LEADER_TIMEOUT_MS passes with no heartbeat, a follower claims leadership
 *    by broadcasting claim_leader and calling onBecomeLeader().
 * 5. If a tab receives claim_leader while it thinks it IS the leader, it steps
 *    down (the new tab independently claimed and will start its own heartbeat).
 */

const CHANNEL_NAME = 'navyrx-sse';
const LEADER_HEARTBEAT_MS = 3_000;
const LEADER_TIMEOUT_MS = 7_000;

export type SSEBusMessage =
  | { type: 'event'; name: string; data: unknown }
  | { type: 'heartbeat'; ts: number }
  | { type: 'claim_leader' };

export class SSELeaderElection {
  private readonly channel: BroadcastChannel;
  private _isLeader = false;
  private lastLeaderHeartbeat = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private checkTimer: ReturnType<typeof setInterval> | null = null;
  private readonly onBecomeLeader: () => void;
  private readonly onReceiveEvent: (name: string, data: unknown) => void;

  get isLeader(): boolean {
    return this._isLeader;
  }

  constructor(
    onBecomeLeader: () => void,
    onReceiveEvent: (name: string, data: unknown) => void,
  ) {
    this.onBecomeLeader = onBecomeLeader;
    this.onReceiveEvent = onReceiveEvent;
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = this.handleMessage.bind(this);
    this.startLeaderCheck();
  }

  private handleMessage(e: MessageEvent<SSEBusMessage>): void {
    const msg = e.data;
    if (msg.type === 'heartbeat') {
      this.lastLeaderHeartbeat = msg.ts;
    } else if (msg.type === 'event') {
      if (!this._isLeader) {
        // Followers process events received from the leader tab
        this.onReceiveEvent(msg.name, msg.data);
      }
    } else if (msg.type === 'claim_leader') {
      // Another tab is taking over — step down if we thought we were leading
      if (this._isLeader) this.stepDown();
    }
  }

  private startLeaderCheck(): void {
    // Give existing leader tabs a chance to send a heartbeat before claiming
    this.lastLeaderHeartbeat = Date.now();

    this.checkTimer = setInterval(() => {
      if (this._isLeader) return; // already leading
      if (Date.now() - this.lastLeaderHeartbeat > LEADER_TIMEOUT_MS) {
        this.becomeLeader();
      }
    }, LEADER_HEARTBEAT_MS);
  }

  private becomeLeader(): void {
    this._isLeader = true;
    // Announce leadership so any existing leader steps down
    this.channel.postMessage({ type: 'claim_leader' } satisfies SSEBusMessage);
    // Send heartbeats so followers stay quiet
    this.heartbeatTimer = setInterval(() => {
      this.channel.postMessage({ type: 'heartbeat', ts: Date.now() } satisfies SSEBusMessage);
    }, LEADER_HEARTBEAT_MS);
    this.onBecomeLeader();
  }

  private stepDown(): void {
    this._isLeader = false;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /** Broadcast an event to all follower tabs. Only effective when this tab is the leader. */
  broadcastEvent(name: string, data: unknown): void {
    if (this._isLeader) {
      this.channel.postMessage({ type: 'event', name, data } satisfies SSEBusMessage);
    }
  }

  destroy(): void {
    this.stepDown();
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
    this.channel.close();
  }
}
